/* Oś czasu „Jak działa" — postęp sterowany scrollem.

   Kroki przygasają, a ten, do którego doszedł czytelnik, wraca do pełnej
   skali; między kropkami dorysowuje się wiśniowa linia — „droga z BrainJellem".

   Stan przygaszenia włącza dopiero skrypt (atrybut data-bj-ready na hoście).
   Bez JS-a i dla robotów indeksujących wszystkie kroki są normalnie widoczne,
   więc treść nigdy nie przepada. Wyliczenia siedzą w rAF i chodzą tylko wtedy,
   gdy sekcja jest w okolicy ekranu. */

/* Kroki aktywują się, gdy kropka minie tę wysokość ekranu — nieco powyżej
   środka, żeby krok zapalał się w momencie czytania, a nie po przewinięciu. */
const ACTIVATION = 0.45;

class BjTimeline extends HTMLElement {
  connectedCallback() {
    this.steps = Array.from(this.querySelectorAll('[data-bj-step]'));
    this.dots = this.steps.map((step) => step.querySelector('[data-bj-dot]'));
    this.progress = this.querySelector('[data-bj-progress]');

    // Jeden krok nie tworzy żadnej drogi — zostawiamy sekcję statyczną.
    if (this.steps.length < 2 || !this.progress || this.dots.some((dot) => !dot)) return;

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);

    this.visible = false;
    this.ticking = false;
    this.active = -1;

    this.setAttribute('data-bj-ready', '');

    this.observer = new IntersectionObserver(
      (entries) => {
        this.visible = entries[0].isIntersecting;
        if (this.visible) this.schedule();
      },
      { rootMargin: '200px 0px' }
    );
    this.observer.observe(this);

    // Zdjęcie w sąsiedniej kolumnie i webfonty zmieniają wysokości już po
    // pierwszym renderze — bez remiaru linia postępu rozjechałaby się z kropkami.
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.schedule(true));
      this.resizeObserver.observe(this);
    }

    addEventListener('scroll', this.onScroll, { passive: true });
    addEventListener('resize', this.onResize);

    this.measure();
    this.update();
  }

  disconnectedCallback() {
    removeEventListener('scroll', this.onScroll);
    removeEventListener('resize', this.onResize);
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
  }

  onScroll() {
    if (this.visible) this.schedule();
  }

  onResize() {
    this.schedule(true);
  }

  schedule(remeasure) {
    if (remeasure) this.stale = true;
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      if (this.stale) {
        this.stale = false;
        this.measure();
      }
      this.update();
    });
  }

  /* Środki kropek liczone względem hosta — skala kroków dotyka tylko treści,
     nie kolumny z kropkami, więc te offsety są stabilne. */
  measure() {
    const host = this.getBoundingClientRect();
    this.height = host.height;
    this.offsets = this.dots.map((dot) => {
      const rect = dot.getBoundingClientRect();
      return rect.top - host.top + rect.height / 2;
    });
  }

  update() {
    if (!this.offsets) return;

    let host = this.getBoundingClientRect();

    // Siatka bez ResizeObservera (albo zmiana, której nie zgłosił: doładowane
    // zdjęcie, webfont, edytor motywu) zostawiłaby offsety sprzed przebudowy
    // i linia rozjechałaby się z kropkami. Wysokość i tak mamy pod ręką,
    // więc pilnujemy jej przy każdej klatce.
    if (this.height !== host.height) {
      this.measure();
      host = this.getBoundingClientRect();
    }
    const cursor = window.innerHeight * ACTIVATION - host.top;
    const first = this.offsets[0];
    const last = this.offsets[this.offsets.length - 1];

    const fill = Math.min(Math.max(cursor - first, 0), last - first);
    this.progress.style.top = `${first}px`;
    this.progress.style.height = `${fill}px`;

    // Przed wejściem sekcji w ekran aktywny zostaje pierwszy krok.
    let index = 0;
    this.offsets.forEach((offset, i) => {
      if (cursor >= offset) index = i;
    });
    if (index === this.active) return;

    this.active = index;
    this.steps.forEach((step, i) => {
      step.dataset.state = i < index ? 'past' : i === index ? 'active' : 'future';
    });
  }
}

if (!customElements.get('bj-timeline')) {
  customElements.define('bj-timeline', BjTimeline);
}
